import React, { useState } from 'react';
import { 
  User, Send, FileText, CheckCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * ReviewAndPresign - Simplified component for reviewing document and sending to signers
 */
const ReviewAndPresign = ({ 
  documentId, 
  fieldsData, 
  onComplete, 
  onCancel,
  documentData = null
}) => {
  const { user } = useAuth();
  
  // Component state - initialize title with document name if available
  const [title, setTitle] = useState(documentData?.name || '');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Separate fields by assignee
  const { signatureFields = [], formFields = [], signedFields = [] } = fieldsData || {};
  
  // Get all signers (including current user if they have fields)
  const allSigners = new Map();
  [...signatureFields, ...formFields].forEach(field => {
    if (field.assignedTo) {
      const email = field.assignedTo.email;
      if (!allSigners.has(email)) {
        allSigners.set(email, {
          name: field.assignedTo.name,
          email: field.assignedTo.email,
          isCurrentUser: field.assignedTo.isCurrentUser || field.assignedTo.email === user?.email,
          fields: []
        });
      }
      allSigners.get(email).fields.push(field);
    }
  });
  
  const signersList = Array.from(allSigners.values());
  
  // Handle sending document
  const handleSendDocument = () => {
    setIsLoading(true);
    
    const finalData = {
      ...fieldsData,
      title,
      message
    };
    
    onComplete(finalData);
  };
  
  return (
    <div className="w-full max-w-2xl mx-auto">
      <div
        className="rounded-3xl border shadow-xl p-8"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(249, 250, 251, 0.95))',
          borderColor: 'rgba(148, 163, 184, 0.2)',
        }}
      >
        <div className="flex items-start justify-between mb-6 pb-4 border-b" style={{ borderColor: 'rgba(148,163,184,0.15)' }}>
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900">Review and Send</h2>
            <p className="text-sm text-gray-500 mt-1">Confirm the details and send your document for signatures.</p>
          </div>
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-50">
            <CheckCircle className="text-green-600" size={24} />
          </div>
        </div>

        <div className="space-y-6">
          {/* Document Name */}
          <div>
            <h3 className="text-sm font-medium mb-3 text-gray-800">Document Name</h3>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter document name"
              className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              style={{ fontSize: '14px' }}
            />
            <p className="text-xs text-gray-500 mt-2">This will be the name of your document and the email subject line.</p>
          </div>

          {/* Signers */}
          <div>
            <h3 className="text-sm font-medium mb-3 text-gray-800">Document will be sent to</h3>
            <div className="space-y-3">
              {signersList.map((signer, index) => (
                <div key={index} className="flex items-center p-3 rounded-xl bg-gray-50 border border-gray-200">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 mr-3">
                    <User size={18} className="text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {signer.name} {signer.isCurrentUser && '(You)'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {signer.email}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 bg-white px-2 py-1 rounded-lg">
                    {signer.fields.length} field{signer.fields.length !== 1 ? 's' : ''}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Message */}
          <div>
            <h3 className="text-sm font-medium mb-3 text-gray-800">Message to Signers (Optional)</h3>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a personal message for the signers..."
              rows={4}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
              style={{ fontSize: '14px' }}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t" style={{ borderColor: 'rgba(148,163,184,0.15)' }}>
          <button
            onClick={onCancel}
            className="px-6 py-3 rounded-xl text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 transition-all font-medium"
          >
            Back to Fields
          </button>
          
          <button
            onClick={handleSendDocument}
            disabled={isLoading || !title.trim()}
            className="relative inline-flex items-center justify-center cursor-pointer select-none rounded-xl px-8 py-3 text-white font-medium shadow-sm transition-all duration-300 group disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: isLoading || !title.trim() ? '#9CA3AF' : 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
              boxShadow: isLoading || !title.trim() ? 'none' : '0 8px 25px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
            }}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                Sending...
              </>
            ) : (
              <>
                <Send size={18} className="mr-2" />
                Send for Signature
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewAndPresign;
